<template>
	<section class="pane" :class="`pane-${pane}`">
		<!-- 只有一個視窗時沒有東西要辨識，不給標題列 -->
		<div v-if="showHead" class="pane-head">
			<span class="pane-title">
				<template v-if="pane === 'tts'">
					<span class="pane-title-full">{{ t('Pane.Tts') }}</span>
					<span class="pane-title-short">{{ t('Pane.TtsShort') }}</span>
				</template>
				<template v-else>{{ t(pane === 'source' ? 'Pane.Source' : 'Pane.Translation') }}</template>
			</span>
			<span class="pane-actions">
				<slot name="actions"></slot>
			</span>
		</div>
		<div :id="`${pane}Scroll`" ref="scrollRef" class="pane-body transcript-scroll" @scroll="handleScroll">
			<div ref="innerRef" class="transcript-inner" :class="{ 'is-no-speakers': !showSpeakers }">
				<!-- 沒被 40 段上限截掉時，譯文最上面標出開頭 -->
				<div v-if="pane === 'translation' && !hasMore" class="transcript-start">
					{{ t('Transcript.Start') }}
				</div>
				<div v-for="item in items" :id="`${pane}-${item.index}`" :key="item.key" class="utterance"
					 :lang="lang" :class="{
						 'is-continued': item.isContinued,
						 'is-latest': item.index === latestIndex,
						 'is-new': item.key === newKey,
						 'is-playing': pane === 'tts' && item.index === activeIndex,
						 'is-read': pane === 'tts' && item.index !== activeIndex,
					 }" @click="handleUtteranceOnClick(item.index, $event)">
					<SpeakerLabel v-if="showSpeakers" :name="item.isContinued ? '' : item.speaker"
								  :color-no="item.speakerNo" />
					<div class="utterance-text">
						<!--正在唸的那一段：已唸與未唸分色，只揭露到正在唸那行的下一行-->
						<p v-if="pane === 'tts' && item.index === activeIndex" class="paragraph-text"><span class="tts-read">{{ reveal.read }}</span><span class="tts-unread">{{ reveal.unread }}</span></p>
						<p v-else class="paragraph-text">{{ item.text }}</p>
						<!--量行用的鏡像：整段完整譯文，高度歸零不佔版面-->
						<p v-if="pane === 'tts' && item.index === activeIndex" class="paragraph-text tts-measure"
						   aria-hidden="true">{{ item.text }}</p>
					</div>
				</div>
				<!-- 翻譯中指示器：跟在逐字稿最後，TTS 視窗沒有，純裝飾不給螢幕閱讀器念 -->
				<div v-if="pane !== 'tts'" class="ellipsis-loader" aria-hidden="true"
					 :class="{ 'is-plain': pane === 'source', 'is-active': translating }">
					<span></span><span></span><span></span>
				</div>
			</div>
		</div>
		<!-- 頂部漸層與捲動指示器，TTS 視窗不給使用者捲所以不需要 -->
		<template v-if="pane !== 'tts'">
			<div class="transcript-fade-top" :class="{ 'is-visible': isFadeVisible }"></div>
			<div class="scroll-indicator" :class="{ 'is-visible': isIndicatorVisible }" :style="indicatorStyle"></div>
		</template>
		<!-- 回到最新：離底就出現，點了平滑捲到底 -->
		<button v-if="pane === 'translation'" class="scroll-to-bottom-button" type="button"
				:class="{ 'is-hidden': !showScrollToBottom }" :title="t('Tips.Icon.ScrollToBottom')"
				:aria-label="t('Tips.Icon.ScrollToBottom')" @click="handleScrollToBottomOnClick">
			<span aria-hidden="true" v-html="icons['scroll-down']" />
		</button>
	</section>
</template>
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { icons } from '@shared-assets/icons-v2'
import SpeakerLabel from './SpeakerLabel.vue'
import { measureLineEnds, revealCut, revealedCharsOf } from './ttsLineMeasure'

/**
 * @interface PaneItem 視窗裡的一句話
 */
export interface PaneItem {
	/**
	 * 訊息 id，v-for 的 key，也用來判斷最後一句有沒有換人
	 */
	key: string
	/**
	 * 在 messages 裡的絕對索引
	 */
	index: number
	/**
	 * 語者名稱，unknown 與空字串已經換成空字串
	 */
	speaker: string
	/**
	 * 語者在講者清單中的序號（從 1 開始）
	 */
	speakerNo: number
	/**
	 * 該視窗要顯示的文字
	 */
	text: string
	/**
	 * 是否與上一句同一位語者
	 */
	isContinued: boolean
}

/**
 * @interface TtsRevealClock TTS 視窗逐行揭露吃的播放進度
 */
export interface TtsRevealClock {
	/**
	 * 音檔目前播放秒數
	 */
	currentTime: number
	/**
	 * 音檔總秒數，不可用（NaN、0、Infinity）時整段直接顯示
	 */
	duration: number
}

/**
 * 進場動畫長度，CSS 的 utterance-in 用同一個值，兩邊要一起改
 */
const REVEAL_MS = 340

/**
 * 捲動指示器淡出的等待時間
 */
const INDICATOR_HIDE_MS = 700

/**
 * 離底部小於這個距離就算貼底，不跟上次程式設定的位置比對（手機的 scrollTop 是小數）
 */
const BOTTOM_THRESHOLD = 4

/**
 * 逐字稿視窗：原文、譯文、TTS 三個視窗共用，各自捲動各自貼底
 */
const props = defineProps<{
	/**
	 * 視窗種類
	 */
	pane: 'source' | 'translation' | 'tts'
	/**
	 * 要渲染的句子，已經依 40 段上限截好
	 */
	items: PaneItem[]
	/**
	 * 前面是否還有被 40 段上限截掉的句子
	 */
	hasMore: boolean
	/**
	 * 是否顯示標題列
	 */
	showHead: boolean
	/**
	 * 頻道是否有語者分離，沒有的話整個語者欄消失
	 */
	showSpeakers: boolean
	/**
	 * 是否有句子正在翻譯，原文與譯文視窗用來顯示三點指示器
	 */
	translating: boolean
	/**
	 * 句子的語言，中日韓共用碼位但字形不同，要靠 lang 挑字型
	 */
	lang?: string
	/**
	 * 目前最新或正在唸的那一句的索引，不帶就是最後一句
	 */
	activeIndex?: number | null
	/**
	 * TTS 視窗正在唸那一段的播放進度；null 或不帶就整段顯示
	 */
	revealClock?: TtsRevealClock | null
}>()

/** 點 TTS 視窗某一段時通知頁面，帶該段的絕對索引 */
const emit = defineEmits<{ select: [index: number] }>()

/** 取得 i18n */
const { t } = useI18n()

/** 捲動區元素 */
const scrollRef = ref<HTMLElement | null>(null)
/** 捲動區裡的內容元素 */
const innerRef = ref<HTMLElement | null>(null)
/** 是否自動貼底，只有譯文視窗會被使用者上捲關掉 */
const allowAutoScroll = ref(true)
/** 內容是否比視窗高 */
const canScroll = ref(false)
/** 剛長出來的那一句的 key，進場動畫期間才有值 */
const newKey = ref('')
/** 捲動指示器是否可見 */
const isIndicatorVisible = ref(false)
/** 捲動指示器的高度與位置 */
const indicatorStyle = ref<{ height?: string; top?: string }>({})
/** 頂部漸層是否可見 */
const isFadeVisible = ref(false)
/** 正在唸那一段量出的行邊界，key 對不上目前那一段就視為還沒量 */
const measured = ref<{ key: string; ends: number[] }>({ key: '', ends: [] })
/** 收起進場旗標的計時器 */
let newKeyTimer: ReturnType<typeof setTimeout> | undefined
/** 收起捲動指示器的計時器 */
let indicatorTimer: ReturnType<typeof setTimeout> | undefined
/** 尺寸變化觀察器 */
let resizeObserver: ResizeObserver | null = null

/** 最新一句的索引，TTS 視窗用正在唸的那一句 */
const latestIndex = computed(() => props.activeIndex ?? props.items[props.items.length - 1]?.index)
/** 最後一句的 key，變了才算有新句進來 */
const lastKey = computed(() => props.items[props.items.length - 1]?.key)
/** TTS 視窗正在唸的那一段，沒有就是 undefined */
const activeTtsItem = computed(() =>
	props.pane === 'tts' ? props.items.find((item) => item.index === props.activeIndex) : undefined,
)
/** 已唸字數：由播放進度換算，是字數而不是行號，字級或視窗寬度變了也不會跑掉；null 代表整段顯示 */
const revealedChars = computed(() => {
	const item = activeTtsItem.value
	if (!item || !props.revealClock) {
		return null
	}

	return revealedCharsOf(item.text.length, props.revealClock.currentTime, props.revealClock.duration)
})
/** 正在唸那一段看得到的文字：已唸的字加上揭露到下一行為止的未唸字 */
const reveal = computed(() => {
	const item = activeTtsItem.value
	if (!item) {
		return { read: '', unread: '' }
	}

	// 這一段的行邊界要是量給目前這段文字的才算數
	const ends = measured.value.key === `${item.key}|${item.text}` ? measured.value.ends : []
	const chars = revealedChars.value
	const cut = chars === null ? null : revealCut(ends, chars)
	// 整段顯示：沒進度、duration 不可用或還沒量到行
	if (chars === null || cut === null) {
		return { read: item.text, unread: '' }
	}

	return { read: item.text.slice(0, chars), unread: item.text.slice(chars, cut) }
})
/** 回到最新浮鈕：離底而且真的捲得動才顯示 */
const showScrollToBottom = computed(() => canScroll.value && !allowAutoScroll.value)

/**
 * 量正在唸那一段的行邊界；行會隨字級與視窗寬度變，所以尺寸一變就要重量
 * @return {void}
 */
const measureTtsLines = (): void => {
	// 只有 TTS 視窗需要量
	const item = activeTtsItem.value
	if (!item) {
		return
	}

	// 鏡像段落裡的文字節點
	const node = scrollRef.value?.querySelector('.utterance.is-playing .tts-measure')?.firstChild as Text | null
	const key = `${item.key}|${item.text}`
	const ends = measureLineEnds(node ?? null)
	// 結果沒變就不更新，避免多餘的重繪連鎖
	if (measured.value.key === key && measured.value.ends.join() === ends.join()) {
		return
	}
	measured.value = { key, ends }
}

/**
 * 點 TTS 視窗的某一段：通知頁面從那段開始播；點到語者標籤等 data-action 控制項時不跳播
 * @param index {number} 該段在 messages 裡的絕對索引
 * @param event {MouseEvent} 點擊事件
 * @return {void}
 */
const handleUtteranceOnClick = (index: number, event: MouseEvent): void => {
	// 只有 TTS 視窗的段落可以點
	if (props.pane !== 'tts') {
		return
	}

	// 點到段落內自帶動作的控制項（語者標籤）時，由它自己處理，不跳播
	if ((event.target as Element | null)?.closest('[data-action]')) {
		return
	}

	// 通知頁面從這一段開始播
	emit('select', index)
}

/**
 * 貼到底部；譯文視窗在使用者往上捲時不動
 * @return {void}
 */
const pinToBottom = (): void => {
	// 還沒掛載就不做事
	const node = scrollRef.value
	if (!node) {
		return
	}

	// 譯文視窗尊重使用者主動往上捲的位置
	if (props.pane === 'translation' && !allowAutoScroll.value) {
		return
	}

	// 直接設 scrollTop，不用 scrollIntoView
	node.scrollTop = node.scrollHeight
	// 順手更新浮鈕要不要出現
	canScroll.value = node.scrollHeight > node.clientHeight
}

/**
 * 強制回到最底並恢復自動貼底，換翻譯語言時用
 * @return {void}
 */
const forceBottom = (): void => {
	// 恢復自動貼底
	allowAutoScroll.value = true
	// 貼到底
	pinToBottom()
}

/**
 * 捲動事件：判斷是否貼底、更新浮鈕、指示器與頂部漸層
 * @return {void}
 */
const handleScroll = (): void => {
	// 還沒掛載或沒有內容時不處理
	const node = scrollRef.value
	if (!node || props.items.length === 0) {
		return
	}

	// 取得捲動位置與尺寸
	const { scrollTop, scrollHeight, clientHeight } = node
	canScroll.value = scrollHeight > clientHeight
	isFadeVisible.value = scrollTop > 1
	// 譯文視窗：離底小於 4px 恢復自動貼底，往上捲就停住
	if (props.pane === 'translation') {
		allowAutoScroll.value = scrollHeight - (scrollTop + clientHeight) < BOTTOM_THRESHOLD
	}
	// TTS 視窗不給使用者捲，沒有指示器
	if (props.pane === 'tts') {
		return
	}

	// 捲動指示器：捲動當下淡入，停止後淡出；用絕對定位疊在視窗上，不參與版面
	// 內容沒超出就不顯示
	if (scrollHeight <= clientHeight) {
		isIndicatorVisible.value = false
		return
	}

	// 軌道上下各留 4px
	const track = clientHeight - 8
	// 滑塊至少 24px
	const thumb = Math.max(24, (clientHeight / scrollHeight) * track)
	// 目前捲動比例
	const ratio = scrollTop / (scrollHeight - clientHeight)
	// 更新滑塊高度與位置
	indicatorStyle.value = { height: `${thumb}px`, top: `${4 + ratio * (track - thumb)}px` }
	// 顯示指示器
	isIndicatorVisible.value = true
	// 清掉上一個淡出計時
	clearTimeout(indicatorTimer)
	// 停止捲動 700ms 後淡出
	indicatorTimer = setTimeout(() => {
		isIndicatorVisible.value = false
	}, INDICATOR_HIDE_MS)
}

/**
 * 點回到最新：恢復自動貼底並平滑捲到底
 * @return {void}
 */
const handleScrollToBottomOnClick = (): void => {
	// 恢復自動貼底，浮鈕跟著隱藏
	allowAutoScroll.value = true
	// 平滑捲到底
	const node = scrollRef.value
	node?.scrollTo({ top: node.scrollHeight, behavior: 'smooth' })
}

// 最後一句換了就是新句：掛載與同一句改字、整批換語言都不算
watch(lastKey, (key) => {
	// 清空了不需要進場
	if (!key) {
		return
	}

	// 標記新句，這個 watch 在渲染前執行，同一輪渲染就會帶上 is-new
	newKey.value = key
	// 動畫結束後收掉旗標
	clearTimeout(newKeyTimer)
	newKeyTimer = setTimeout(() => {
		newKey.value = ''
	}, REVEAL_MS)
})

// DOM 帶上 is-new 之後才量高度播動畫
watch(newKey, (key) => {
	// 旗標收掉時不處理
	if (!key) {
		return
	}

	// 使用者要求減少動態時不播
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		return
	}

	// 找不到剛長出來的那一句就不播
	const fresh = scrollRef.value?.querySelector<HTMLElement>('.utterance.is-new')
	if (!fresh) {
		return
	}

	// 新句進場：margin-bottom 從負的句高長回 0，逐字稿貼著底部所以上面的內容會被往上推
	// 刻意不碰 scrollTop，離底超過門檻會被判成使用者上捲而鎖死自動貼底，貼底交給 ResizeObserver 逐幀處理
	// 這一句佔掉的高度＝它自己加上與上一句的間距
	const delta = fresh.offsetHeight + (parseFloat(getComputedStyle(fresh).marginTop) || 0)
	// 播進場動畫
	fresh.animate(
		[{ marginBottom: `${-delta}px` }, { marginBottom: '0px' }],
		{ duration: REVEAL_MS, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
	)
}, { flush: 'post' })

// 內容更新後重新貼底
watch(() => props.items, pinToBottom, { flush: 'post' })

// 正在唸的那一段換了（換段或文字變了），DOM 更新後重量行
watch(() => [activeTtsItem.value?.key, activeTtsItem.value?.text], measureTtsLines, { flush: 'post' })

// 掛載後先貼底，並觀察捲動區與內容的尺寸變化
onMounted(() => {
	// 先貼底
	pinToBottom()
	// TTS 視窗掛載時先量一次行
	measureTtsLines()
	// 環境沒有 ResizeObserver 時只靠內容更新時貼底
	if (typeof ResizeObserver === 'undefined') {
		return
	}

	// RWD 換行、字級與段距調整、視窗縮放都會改尺寸，尺寸一變就重新貼底
	resizeObserver = new ResizeObserver(() => {
		// 先貼底
		pinToBottom()
		// 字級或寬度變了，TTS 視窗的行邊界跟著變，重新量
		measureTtsLines()
	})
	if (scrollRef.value) {
		resizeObserver.observe(scrollRef.value)
	}
	if (innerRef.value) {
		resizeObserver.observe(innerRef.value)
	}
})

// 卸載時收掉觀察器與計時器
onBeforeUnmount(() => {
	resizeObserver?.disconnect()
	clearTimeout(newKeyTimer)
	clearTimeout(indicatorTimer)
})

// 讓頁面能在換語言時強制貼底
defineExpose({ forceBottom })
</script>
<style scoped lang="scss">
// 視窗：白底、圓角 16、外框 1px，自己也是 container，標題列才能依這一格多寬換短標題
.pane {
	container-type: inline-size;
	container-name: pane-box;
	position: relative;
	min-height: 0;
	min-width: 0;
	display: flex;
	flex-direction: column;
	background: var(--pane-bg);
	border-radius: var(--radius-pane);
	border: 1px solid var(--ui-grey-lightest);
	overflow: hidden;
}

// 標題列：高度交給 min-height、置中交給 align-items，上下不給 padding 否則有按鈕的 TTS 會多 1px
.pane-head {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	min-height: var(--pane-head-height);
	padding: 0 var(--pane-head-padding-right) 0 var(--pane-head-padding-left);
	border-bottom: 1px solid var(--ui-grey-lightest);
}

// 標題可壓縮，寬度不夠先截標題，不要把右邊的按鈕擠出邊界
.pane-title {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	font-size: var(--text-pane-title-size);
	font-weight: var(--text-pane-title-weight);
	white-space: nowrap;
	color: var(--text-body-01);
}

.pane-title-short {
	display: none;
}

// 視窗窄到放不下「Text to Speech 加兩顆按鈕」就換成 TTS
@container pane-box (max-width: 280px) {
	.pane-title-full {
		display: none;
	}

	.pane-title-short {
		display: inline;
	}
}

// 標題列右側的按鈕群，按鈕不壓縮，要讓的是左邊的標題
.pane-actions {
	display: flex;
	align-items: center;
	gap: 4px;
	flex-shrink: 0;
}

// 內文區：語者標籤要看自己這個視窗有多寬，不是螢幕多寬
.pane-body {
	flex: 1;
	min-height: 0;
	container-type: inline-size;
	container-name: pane;
}

.transcript-scroll {
	overflow-y: auto;
	padding: var(--pane-padding);
	// 內容靠下對齊、往上長；不足一頁時貼底，超過一頁時正常捲動
	display: flex;
	flex-direction: column;
	// 原生捲軸會佔掉版面寬度，改用浮層指示器
	scrollbar-width: none;
	// 捲到頭時不要把剩下的位移傳給外層，手勢只吃垂直
	overscroll-behavior: contain;
	// 留著 pinch-zoom，畫面被放大時在這裡雙指也縮得回來
	touch-action: pan-y pinch-zoom;
	-webkit-overflow-scrolling: touch;

	&::-webkit-scrollbar {
		display: none;
	}
}

.transcript-inner {
	margin-top: auto;

	// 頻道沒有語者分離時整欄消失，文字與三點回到視窗邊緣
	&.is-no-speakers {
		--speaker-column: 0px;
		--speaker-gap: 0px;
	}
}

// 視窗內文區窄於 440 時語者縮成圓點，欄寬跟著縮
@container pane (max-width: 440px) {
	.transcript-inner:not(.is-no-speakers) {
		--speaker-column: var(--speaker-column-narrow);
	}
}

// 原文視窗開著時三個視窗一律用圓點，內容與上面那組相同，改一邊要記得改另一邊
.has-source .transcript-inner:not(.is-no-speakers) {
	--speaker-column: var(--speaker-column-narrow);
}

// 單句：語者欄在左、文字在右
.utterance {
	position: relative;
	display: flex;
	align-items: flex-start;
	gap: var(--speaker-gap);
	font-size: var(--transcript-font-size);

	// 段距由控制列的 Spacing 調整，三個視窗一起吃
	& + .utterance {
		margin-top: var(--paragraph-gap);
	}

	// 新冒出來的那一句：淡入加往上滑一小段
	&.is-new {
		animation: utterance-in 340ms cubic-bezier(0.22, 1, 0.36, 1);
	}
}

@keyframes utterance-in {
	from {
		opacity: 0;
		transform: translateY(10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@media (prefers-reduced-motion: reduce) {
	.utterance.is-new {
		animation: none;
	}
}

.utterance-text {
	flex: 1;
	min-width: 0;
}

.paragraph-text {
	margin: 0;
	line-height: var(--transcript-line-height);
	color: var(--text-body-01);
}

// 泰文與天城體的母音記號會堆疊，行高要拉開
.utterance:lang(th) .paragraph-text,
.utterance:lang(hi) .paragraph-text,
.utterance:lang(mr) .paragraph-text {
	line-height: var(--transcript-line-height-stacked);
}

// 原文：最新黑、被推上去的灰
.pane-source .paragraph-text {
	color: var(--ui-grey-light);
}

.pane-source .utterance.is-latest .paragraph-text {
	color: var(--text-body-01);
}

// 譯文：最新藍、被推上去的淡藍
.pane-translation .paragraph-text {
	color: var(--translation-text-past);
}

.pane-translation .utterance.is-latest .paragraph-text {
	color: var(--translation-text-latest);
}

// TTS：整片翻譯藍；唸過的白、揭露出來還沒唸到的淡藍
.pane-tts {
	background: var(--tts-bg);
	border-color: var(--tts-bg);

	.pane-head {
		border-bottom-color: rgba(255, 255, 255, 0.25);
	}

	.pane-title {
		color: #fff;
	}

	.paragraph-text {
		color: var(--tts-text-read);
	}

	.tts-read {
		color: var(--tts-text-read);
	}

	.tts-unread {
		color: var(--tts-text-unread);
	}

	// 每一段都可以點，跳到那段開始播
	.utterance {
		cursor: pointer;
	}
}

// 量行用的鏡像：放整段完整譯文，寬度與字級跟看得到的那段一致，絕對定位所以不佔版面，
// visibility:hidden 之後仍可用 Range 量座標；height:0 加 overflow:hidden 不能拿掉，
// 否則它比看得到的那段高，會撐大捲動區的 scrollHeight，貼底就貼到一片空白上
.pane-tts .utterance-text {
	position: relative;
}

.tts-measure {
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 0;
	overflow: hidden;
	visibility: hidden;
	pointer-events: none;
}

// TTS 不給使用者捲：視窗多大就顯示多少，塞不下的從上面裁掉；程式設定 scrollTop 仍然有效，貼底照舊
.pane-tts .transcript-scroll {
	overflow: hidden;
}

// 翻譯中指示器：三顆 6px 圓點，左緣對齊逐字稿文字再往右 4
// 永遠佔位只切 opacity，版面高度不隨指示器變動，元素一直在 box tree 裡所以三點動畫不中斷
.ellipsis-loader {
	display: flex;
	gap: 8px;
	align-items: center;
	height: 16px;
	margin-top: 12px;
	margin-left: calc(var(--speaker-column) + var(--speaker-gap) + 4px);
	opacity: 0;
	transition: opacity 180ms ease;

	// 翻譯中時淡入顯示
	&.is-active {
		opacity: 1;
	}

	// 原文視窗的三點不跟著翻譯色走，改成灰底黑高峰
	&.is-plain {
		--ellipsis-dot-color: var(--ui-grey-lighter);
		--ellipsis-dot-active-color: var(--text-body-01);
	}

	span {
		width: 6px;
		height: 6px;
		background-color: var(--ellipsis-dot-color);
		border-radius: 50%;
		transform-origin: center;
		will-change: transform, background-color;
		animation: dot-step 1800ms linear infinite;

		// 三顆點跑同一條時間軸，各差 250ms，波形由左往右走
		&:nth-child(1) {
			animation-delay: 0ms;
		}

		&:nth-child(2) {
			animation-delay: 250ms;
		}

		&:nth-child(3) {
			animation-delay: 500ms;
		}
	}
}

// 基準 800ms、上升 250ms、高峰、消退 250ms，一輪 1800ms；高峰放大到 8px 是 scale 1.3333
@keyframes dot-step {
	0%,
	44.444% {
		transform: scale(1);
		background-color: var(--ellipsis-dot-color);
		animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
	}

	58.333% {
		transform: scale(1.3333);
		background-color: var(--ellipsis-dot-active-color);
		animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
	}

	72.222%,
	100% {
		transform: scale(1);
		background-color: var(--ellipsis-dot-color);
	}
}

@media (prefers-reduced-motion: reduce) {
	// 減少動態時不做淡入淡出，直接切換
	.ellipsis-loader {
		transition: none;
	}

	.ellipsis-loader span {
		animation: none;
	}
}

// 到頭的訊號：譯文最上面的開頭標記
.transcript-start {
	margin-bottom: 16px;
	text-align: center;
	font-size: 12px;
	color: var(--ui-grey-lighter);
}

// 頂部漸層只在上面還有內容時出現，有標題列時標題列的底線已經交代清楚，就不需要
.transcript-fade-top {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 32px;
	z-index: 2;
	pointer-events: none;
	background: linear-gradient(to bottom, #fff 0%, rgba(255, 255, 255, 0) 100%);
	opacity: 0;
	transition: opacity 0.18s ease;

	&.is-visible {
		opacity: 1;
	}
}

.pane:has(.pane-head) .transcript-fade-top {
	display: none;
}

// 捲動指示器：捲動時才淡入，絕對定位疊在視窗上，出現與消失都不影響內容
.scroll-indicator {
	position: absolute;
	right: 4px;
	width: 4px;
	border-radius: 2px;
	background: rgba(21, 21, 21, 0.25);
	opacity: 0;
	transition: opacity 0.25s ease;
	pointer-events: none;

	&.is-visible {
		opacity: 1;
	}
}

// 指示器要讓開標題列
.pane:has(.pane-head) .scroll-indicator {
	top: var(--pane-head-height);
}

@media (prefers-reduced-motion: reduce) {
	.transcript-fade-top {
		transition: none;
	}
}

// 回到最新浮鈕：視窗內置中靠下，淡入淡出加些微位移，不要瞬間出現
.scroll-to-bottom-button {
	position: absolute;
	bottom: 16px;
	left: 50%;
	z-index: 3;
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: none;
	padding: 0;
	border-radius: 50%;
	background: #fff;
	color: var(--text-body-01);
	// 灰框在白色逐字稿上太弱，用陰影把它抬離文字
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
	cursor: pointer;
	transform: translate(-50%, 0);
	transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;

	:deep(svg) {
		width: 24px;
		height: 24px;
	}

	&.is-hidden {
		opacity: 0;
		visibility: hidden;
		transform: translate(-50%, 6px);
		pointer-events: none;
	}
}

@media (prefers-reduced-motion: reduce) {
	.scroll-to-bottom-button,
	.scroll-to-bottom-button.is-hidden {
		transition: opacity 0.2s linear;
		transform: translate(-50%, 0);
	}
}
</style>
